import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ArrowLeftRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { cn } from "@/lib/utils";
import { useTRPC } from "@/trpc";
import { OrganizationWageSetOptionLabel } from "../../../components/OrganizationWageSetOptionLabel";
import type { OrgPendingTimeEntry } from "../../hooks/usePendingTimeEntries";

const CONFLICT_ERROR_CODE = "CONFLICT";

type EntryOrganizationWageSet = NonNullable<
  OrgPendingTimeEntry["organizationWageSet"]
>;

interface SwitchOrganizationWageSetButtonProps {
  entry: OrgPendingTimeEntry;
  organizationWageSet: EntryOrganizationWageSet;
  organizationBusinessUnitId: string;
  onSaved: () => void;
}

export function SwitchOrganizationWageSetButton({
  entry,
  organizationWageSet,
  organizationBusinessUnitId,
  onSaved,
}: SwitchOrganizationWageSetButtonProps) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        className="h-7 px-2 text-xs text-slate-600"
        onClick={() => setOpen(true)}
      >
        <ArrowLeftRight className="mr-1 h-3 w-3" />
        Switch wage set
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md">
          <SwitchOrganizationWageSetForm
            entry={entry}
            organizationWageSet={organizationWageSet}
            organizationBusinessUnitId={organizationBusinessUnitId}
            onSaved={onSaved}
            onClose={() => setOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}

interface SwitchOrganizationWageSetFormProps
  extends SwitchOrganizationWageSetButtonProps {
  onClose: () => void;
}

// Rendered inside DialogContent so its state resets each time the dialog opens.
function SwitchOrganizationWageSetForm({
  entry,
  organizationWageSet,
  organizationBusinessUnitId,
  onSaved,
  onClose,
}: SwitchOrganizationWageSetFormProps) {
  const trpc = useTRPC();
  const [selectedOrganizationWageSetId, setSelectedOrganizationWageSetId] =
    useState(organizationWageSet.organizationWageSetId);

  const { data: organizationWageSets, isLoading } = useQuery(
    trpc.timeEntry.getOrganizationWageSetsForShift.queryOptions({
      organizationBusinessUnitId,
      shiftId: entry.shiftId,
    }),
  );

  const switchMutation = useMutation(
    trpc.timeEntry.updateShiftOrganizationWageSet.mutationOptions({
      onSuccess: () => {
        toast.success("Wage set updated");
        onSaved();
        onClose();
      },
      onError: (error) => {
        toast.error(error.message || "Failed to switch wage set");
        // The wage set changed since the reviewer loaded it, so show the current one.
        if (error.data?.code === CONFLICT_ERROR_CODE) {
          onSaved();
          onClose();
        }
      },
    }),
  );

  const handleSave = () => {
    switchMutation.mutate({
      organizationBusinessUnitId,
      shiftId: entry.shiftId,
      organizationWageSetId: selectedOrganizationWageSetId,
      expectedOrganizationWageSetVersionId: organizationWageSet.versionId,
    });
  };

  const hasChanged =
    selectedOrganizationWageSetId !== organizationWageSet.organizationWageSetId;

  return (
    <>
      <DialogHeader>
        <DialogTitle>Switch wage set</DialogTitle>
        <DialogDescription>
          Choose which of the provider's wage sets prices these hours.
        </DialogDescription>
      </DialogHeader>

      {isLoading && (
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading wage sets...
        </div>
      )}
      {organizationWageSets?.length === 0 && (
        <p className="text-sm text-slate-500">
          The provider has no wage sets for this work date.
        </p>
      )}
      {organizationWageSets && organizationWageSets.length > 0 && (
        <RadioGroup
          value={selectedOrganizationWageSetId}
          onValueChange={setSelectedOrganizationWageSetId}
          disabled={switchMutation.isPending}
        >
          {organizationWageSets.map((orgWageSet) => {
            const optionId = `switch-wage-set-${orgWageSet.organizationWageSetId}`;
            const unpayableNoteId = `${optionId}-unpayable`;
            // The server refuses a set that has no wage for these hours'
            // wage type and overtime, so offer it only as information.
            const isUnpayable = !orgWageSet.canPayPendingHours;
            return (
              <div
                key={orgWageSet.organizationWageSetId}
                className="flex items-start gap-2 rounded-md border p-3"
              >
                <RadioGroupItem
                  id={optionId}
                  value={orgWageSet.organizationWageSetId}
                  disabled={isUnpayable}
                  aria-describedby={isUnpayable ? unpayableNoteId : undefined}
                  className="mt-1"
                />
                <Label
                  htmlFor={optionId}
                  className={cn(
                    "min-w-0 font-normal",
                    isUnpayable ? "cursor-not-allowed" : "cursor-pointer",
                  )}
                >
                  <span className={cn(isUnpayable && "opacity-60")}>
                    <OrganizationWageSetOptionLabel
                      name={orgWageSet.name}
                      wages={orgWageSet.wages}
                    />
                  </span>
                  {isUnpayable && (
                    <span
                      id={unpayableNoteId}
                      className="mt-1 block text-xs text-amber-700"
                    >
                      No wage for these hours' wage type or overtime
                    </span>
                  )}
                </Label>
              </div>
            );
          })}
        </RadioGroup>
      )}

      <DialogFooter className="gap-2 sm:gap-0">
        <Button
          variant="outline"
          onClick={onClose}
          disabled={switchMutation.isPending}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={switchMutation.isPending || !hasChanged}
        >
          {switchMutation.isPending && (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          )}
          Switch wage set
        </Button>
      </DialogFooter>
    </>
  );
}
